/**
 * Base-direction resolution for bidirectional text.
 *
 * `dir="auto"` — the browser's built-in answer — is the Unicode **first-strong**
 * heuristic: scan for the first strong character and let it decide the base
 * direction of the whole paragraph. That is the right instrument for exactly one
 * situation, which is the one the HTML spec designed it for: content whose
 * language is genuinely UNKNOWN at author time (a comment, a typed-in name, a
 * search query).
 *
 * It is the wrong instrument the moment the first word is foreign to the rest of
 * the sentence, because a single leading token then flips an entire paragraph.
 * PanLuma hits that constantly: brand names, module names and technical terms
 * stay Latin in every locale (AGENTS.md § i18n — "module names are always
 * literal"), so a Hebrew string beginning with one is misdetected 100% of the
 * time — `"PanLuma הוא מקור האמת"` laid out LTR, putting PanLuma on the wrong
 * side of its own sentence.
 *
 * The fix for our OWN copy is not a better heuristic — it is not to guess at all:
 * a translated string is written in the UI locale, so it takes the document
 * direction (STYLE_GUIDE § Bidirectional Text). This module serves what is left,
 * where guessing is unavoidable: user-generated and tenant-authored text.
 *
 * ## The heuristic: word majority, ties to the reader's locale
 *
 * Count whitespace-delimited WORDS by their own first-strong character and let
 * the majority set the base direction; a tie — or no strong character anywhere —
 * falls back to the locale. Words rather than characters, because character
 * counting re-introduces the same class of error from the other end: Latin words
 * are longer than Hebrew ones, so `"WhatsApp מחובר"` is 8 Latin characters
 * against 5 Hebrew and would resolve LTR, while it is plainly one Hebrew phrase
 * naming one product.
 *
 *   "PanLuma הוא מקור האמת"  → 1 LTR word,  3 RTL words → rtl
 *   "WhatsApp מחובר"         → 1 LTR word,  1 RTL word  → tie → locale
 *   "Acme Corp בע\"מ"        → 2 LTR words, 1 RTL word  → ltr
 *   "SKU-99 מסנן שמן"        → 1 LTR word,  2 RTL words → rtl
 *
 * A tie resolving to the locale is deliberate and is the safe half of the rule:
 * a two-word mixed phrase is overwhelmingly a foreign token plus a word of the
 * reader's own language, and the reader's language is what the phrase is.
 */

/**
 * The strong-RTL scripts. Bidi class R/AL is not expressible as a Unicode
 * property escape, so it is spelled as the scripts that carry it — which is
 * every right-to-left script in living use, plus the Arabic and
 * Hebrew presentation-form blocks (both are `Script=Arabic` / `Script=Hebrew`,
 * so they need no separate arm).
 */
const RTL_SCRIPT =
  /[\p{Script=Hebrew}\p{Script=Arabic}\p{Script=Syriac}\p{Script=Thaana}\p{Script=Nko}\p{Script=Samaritan}\p{Script=Mandaic}\p{Script=Adlam}]/u;

/** Any letter — the strong characters, of either direction. */
const LETTER = /\p{L}/u;

export type BaseDirection = 'ltr' | 'rtl';

/**
 * The base direction of one whitespace-delimited word, from its first strong
 * character (the standard first-strong rule, applied at word scale where a
 * leading foreign token cannot reach past its own word).
 *
 * Returns `null` for a word with no strong character at all — a bare number, a
 * date, punctuation — which is weak and votes for neither side.
 */
function wordDirection(word: string): BaseDirection | null {
  for (const char of word) {
    if (!LETTER.test(char)) continue;
    return RTL_SCRIPT.test(char) ? 'rtl' : 'ltr';
  }
  return null;
}

/**
 * Resolve the base direction `text` should be laid out in.
 *
 * @param text     The content to be rendered.
 * @param fallback The reader's locale direction, used when the text votes for
 *                 neither side (a tie, or no strong characters at all).
 */
export function resolveBaseDirection(text: string, fallback: BaseDirection): BaseDirection {
  let rtl = 0;
  let ltr = 0;

  for (const word of text.split(/\s+/)) {
    const direction = wordDirection(word);
    if (direction === 'rtl') rtl++;
    else if (direction === 'ltr') ltr++;
  }

  if (rtl > ltr) return 'rtl';
  if (ltr > rtl) return 'ltr';
  return fallback;
}

/**
 * The `dir` attribute value for an element rendering `content`.
 *
 * This is the non-hook half of `useAutoDirection` (`shared/components/common`),
 * for a site that cannot call a hook — an element inside a `.map()` or behind a
 * `&&`. Read the locale direction ONCE at the top of the component with
 * `useDirectionSafe()` and pass it in as `fallback`.
 *
 * Returns `'auto'` when `content` is not plain text (see {@link nodeToText}).
 */
export function autoDirection(content: unknown, fallback: BaseDirection): BaseDirection | 'auto' {
  const text = nodeToText(content);
  return text === null ? 'auto' : resolveBaseDirection(text, fallback);
}

/** Unicode bidi isolate controls. LRI/RLI open a directional isolate; PDI closes it. */
const LRI = '⁦';
const RLI = '⁧';
const PDI = '⁩';

/**
 * Isolate an unknown-language token inside a translated sentence that is a PLAIN
 * STRING — a `t()` result with no JSX to wrap.
 *
 * `<BidiText>` and `<LtrToken>` are the answer wherever the token can be its own
 * ELEMENT, and they are what STYLE_GUIDE § Bidirectional Text sends you to. They
 * are unavailable at the sites this exists for: a value handed to `t()` comes
 * back interpolated into a `string`, so by the time anything could wrap it there
 * is no fragment left to wrap. That string then reaches a `title=`, a toast, or a
 * `TooltipContent` whose prop is typed `string` — and under RTL an un-isolated
 * `Receipt-2056-0227-5869.pdf` mid-sentence is exactly the digit+separator run the
 * bidi algorithm reorders.
 *
 * The isolate is chosen by this module's OWN word-majority heuristic rather than
 * by `<bdi>`'s first-strong default (U+2068), so a Hebrew name opening with a
 * Latin SKU isolates as Hebrew — the same answer `<BidiText>` would give the same
 * string. Empty input is returned untouched: wrapping nothing in controls only
 * gives i18next an argument that renders as invisible characters.
 *
 * Prefer `<Trans>` with a `<BidiText>` slot when the sentence is rendered as JSX
 * and you control the markup; reach for this only when the destination is a
 * string.
 */
export function isolateToken(text: string, fallback: BaseDirection): string {
  if (!text) return text;
  const open = resolveBaseDirection(text, fallback) === 'rtl' ? RLI : LRI;
  return `${open}${text}${PDI}`;
}

/**
 * The document's current base direction, read straight off `<html dir>`.
 *
 * This module imports NOTHING, and that is the point: it is what lets
 * `components/ui/*` primitives resolve a direction without importing
 * `DirectionContext`, which reaches `i18n/index` → `i18n/config`, whose module
 * body runs `i18n.use(initReactI18next).init(...)`. `Label` and `TooltipContent`
 * are in almost every component's import graph, so pulling that in made every
 * spec with a partial `vi.mock('react-i18next')` fail on a missing
 * `initReactI18next` export — six `GoogleMap` cases went red on exactly that,
 * from a change that touched neither maps nor i18n.
 *
 * `DirectionProvider` writes `document.documentElement.dir` on every language
 * change, so this is the same value the context carries; components that already
 * import the context should prefer `useDirectionSafe()`.
 */
export function documentDirection(): BaseDirection {
  if (typeof document === 'undefined') return 'ltr';
  return document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
}

/**
 * The `dir` for a GENERIC container primitive — `Label`, `TooltipContent` —
 * whose children may be a translated string, an entity name, or arbitrary
 * markup, with no way for the primitive to know which.
 *
 * Where the children ARE readable as text, resolve them: word majority answers
 * both content classes correctly at once, because a Hebrew UI string is
 * majority-Hebrew and so is a Hebrew entity name. That keeps the safety net
 * these primitives used to provide with their blanket `dir="auto"` — a call site
 * rendering a raw entity name in a `<Label>` or a tooltip stays correct without
 * having to know about `<BidiText>`.
 *
 * Where they are NOT (markup, an icon beside a name, a whole card), return
 * `undefined` — React omits the attribute and the element inherits the document
 * direction. Deliberately **not** `'auto'`: that is exactly the case that broke,
 * since `Label` is also the clickable wrapper around a whole `PolicyCard` and
 * first-strong read the leading `P` of "PanLuma" (WI cf3fc9a4). For a container
 * whose content class was never declared, the document direction is the only
 * safe default.
 *
 * `<BidiText>` differs on purpose: its author HAS declared the content unknown,
 * so unreadable children there fall back to `'auto'` rather than to the locale.
 */
export function containerDirection(
  content: unknown,
  fallback: BaseDirection
): BaseDirection | undefined {
  const text = nodeToText(content);
  return text === null ? undefined : resolveBaseDirection(text, fallback);
}

/**
 * Flatten a React node to the plain text this heuristic can read, or `null` when
 * it cannot be read as text.
 *
 * Callers that get `null` must fall back to native `dir="auto"`: a node holding
 * elements (a `<Trans>` with embedded markup, an icon beside a name) has no
 * string for us to count, and first-strong on the rendered result is still
 * better than assuming the locale direction.
 */
export function nodeToText(node: unknown): string | null {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  // React renders null / undefined / booleans as nothing, so they are
  // readable-and-empty rather than unreadable. The distinction matters inside an
  // array: `{name}{title && <div/>}` with no title is `[name, false]`, and
  // calling that unreadable would throw away a name we can perfectly well read.
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (Array.isArray(node)) {
    const parts = node.map(nodeToText);
    return parts.some((part) => part === null) ? null : parts.join('');
  }
  return null;
}
